import styled from 'styled-components';
import {Link} from 'react-router-dom';
import {media} from '../../styles/components/media-queries';

// Matches the reference design's own .container max-width (1180px).
export const Page = styled.div`
  max-width: 118rem;
  margin: 0 auto;
  color: #132018;
  background: #f2f7f3;
`;

export const Hero = styled.div`
  position: relative;
  overflow: hidden;
  min-height: 42rem;
  border-radius: 1rem;
  display: flex;
  align-items: center;

  ${media.small`
    min-height: 36rem;
    border-radius: 0;
  `};
`;

export const HeroImg = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center 38%;
`;

export const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    90deg,
    rgba(10, 26, 16, 0.86) 0%,
    rgba(10, 26, 16, 0.62) 42%,
    rgba(10, 26, 16, 0.18) 75%,
    rgba(10, 26, 16, 0.05) 100%
  );
`;

export const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  padding: 0 5.6rem;
  max-width: 62rem;

  ${media.small`
    padding: 0 1.8rem;
  `};
`;

// Small "حلال / HALAL" mark that sits above the headline, matching the
// certification badge on the reference design.
export const HeroCert = styled.div`
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  align-self: flex-start;
  line-height: 1;
  margin-bottom: 1.4rem;
  color: ${p => p.theme.primaryTextColor};

  span:first-child {
    font-size: 3rem;
    font-weight: 700;
  }

  span:last-child {
    font-size: 1.1rem;
    font-weight: 800;
    letter-spacing: 0.18rem;
    margin-top: 0.2rem;
  }
`;

export const HeroTitle = styled.h1`
  color: ${p => p.theme.primaryTextColor};
  font-size: 4.2rem;
  font-weight: 700;
  line-height: 1.12;
  margin: 0 0 1rem;

  ${media.small`
    font-size: 3rem;
  `};
`;

export const HeroTitleAccent = styled.span`
  color: #3fa65c;
`;

export const HeroSub = styled.p`
  color: rgba(255, 255, 255, 0.88);
  font-size: 1.6rem;
  line-height: 1.65;
  margin-bottom: 2.8rem;
  max-width: 46rem;

  ${media.small`
    margin-bottom: 1.8rem;
  `};
`;

export const HeroButtons = styled.div`
  display: flex;
  gap: 1.4rem;
  align-items: center;
  flex-wrap: wrap;
`;

export const PrimaryButton = styled(Link)`
  background: #1c6b3a;
  color: ${p => p.theme.primaryTextColor};
  font-size: 1.4rem;
  font-weight: 700;
  padding: 1.3rem 2.8rem;
  border-radius: 0.6rem;
  border: none;
  white-space: nowrap;
  text-decoration: none;
  display: inline-block;

  &:hover {
    background: #144a2b;
  }
`;

export const Wrap = styled.div`
  padding: 0 3.2rem;

  ${media.small`
    padding: 0 2rem;
  `};
`;

export const NumWrap = styled(Wrap)``;

export const Shop = styled.div`
  margin-top: 3.2rem;
`;

export const ShopHeader = styled.div`
  margin-bottom: 1.8rem;
`;

export const Eyebrow = styled.p`
  font-size: 1.3rem;
  font-weight: bolder;
  letter-spacing: 0.14rem;
  color: #1c6b3a;
  margin-bottom: 1rem;
  text-transform: uppercase;
`;

export const ShopHeading = styled.h2`
  font-size: 2.2rem;
  font-weight: 700;
  color: #132018;
`;

// 3 per row, matching the reference design exactly — its cards have no
// image, just a heading, a short description and a "Shop X →" link.
export const CatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;

  ${media.small`
    grid-template-columns: 1fr;
  `};
`;

export const CatTile = styled(Link)`
  background: #fff;
  border: 0.1rem solid #e4e1d8;
  border-radius: 0.8rem;
  cursor: pointer;
  display: block;
  text-decoration: none;
  color: inherit;
  transition: transform 0.15s, box-shadow 0.15s;

  &:hover {
    transform: translateY(-0.2rem);
    box-shadow: 0 0.6rem 2rem rgba(0, 0, 0, 0.1);
  }
`;

export const CatTileBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 2.6rem;

  h4 {
    font-size: 1.9rem;
    font-weight: 700;
    color: #132018;
    margin: 0;
  }

  p {
    font-size: 1.45rem;
    color: #6b7570;
    line-height: 1.5;
  }
`;

// "Shop X →" affordance under a tile's copy — mirrors the reference design's
// category cards.
export const CatTileLink = styled.span`
  display: block;
  color: #1c6b3a;
  font-weight: 700;
  font-size: 1.4rem;
`;

// Full-bleed "View all halal products" row under the category grid — a
// direct child of <Page>, like <Hero>/<FooterCta>/<BestSellersSection>,
// rather than nested inside <Wrap>'s side padding, so its green background
// spans edge to edge instead of leaving a gap on each side.
export const ViewAllBanner = styled.div`
  margin: 2rem 3rem;
  background: #144a2b;
  padding: 2rem 3.2rem;
  display: flex;
  align-items: center;
  border-radius: 1rem;
  justify-content: space-between;
  gap: 1.6rem;
  flex-wrap: wrap;
  ${media.small`
    padding: 2rem;
  `};
`;

export const ViewAllBannerTitle = styled.h3`
  color: ${p => p.theme.primaryTextColor};
  font-size: 1.6rem;
  font-weight: 700;
`;

export const ViewAllBannerButton = styled(Link)`
  background: ${p => p.theme.primaryTextColor};
  color: #144a2b;
  font-size: 1.4rem;
  font-weight: 700;
  padding: 1.1rem 2.2rem;
  border-radius: 0.6rem;
  white-space: nowrap;
  text-decoration: none;
  display: inline-block;

  &:hover {
    background: #f1eee3;
  }
`;

// Full-bleed white band for Best Sellers — a direct child of <Page>, like
// <Hero> and <FooterCta>, rather than nested inside <Wrap>'s padded, page-tint
// column, so its white background isn't inset by that padding on both sides.
export const BestSellersSection = styled.div`
  padding: 3.2rem;
  ${media.small`
    padding: 2rem;
  `};
`;

// Best Sellers — real <Product> cards (see components/Product/Product.js),
// which already own their own card styling (ListProduct's fixed 35rem-wide
// card), so this is just a grid host for them. 3 per row only fits at desktop
// widths — that fixed card width doesn't shrink to fit a column, so on
// tablet (and again on phone) the column count has to drop or the row
// overflows/crams together instead of actually reflowing.
export const ProductsWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  justify-items: center;
  gap: 0.8rem;

  ${media.medium`
    grid-template-columns: repeat(2, 1fr);
  `};

  ${media.small`
    grid-template-columns: 1fr;
  `};
`;

// Matches the reference design's stats row exactly: centred, gapped items
// with a top AND bottom rule (not a per-item side border).
export const Trust = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4.4rem 0;
  border-top: 0.1rem solid rgba(20, 74, 43, 0.12);
  border-bottom: 0.1rem solid rgba(20, 74, 43, 0.12);

  ${media.small`
    gap: 4rem;
    padding: 3rem 0;
  `};
`;

export const TrustItem = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 0.8rem;
`;

export const TrustDivider = styled.div`
  width: 0.1rem;
  height: 6.4rem;
  background: #e4e1d8;
`;

export const TrustNum = styled.div`
  font-size: 4.4rem;
  font-weight: 800;
  color: #144a2b;
  line-height: 1;

  ${media.small`
    font-size: 3.2rem;
  `};
`;

export const TrustNumSmall = styled.span`
  font-size: 1.4rem;
  vertical-align: top;
`;

export const TrustLabel = styled.div`
  font-size: 1.5rem;
  color: #425147;
  padding-top: 1rem;
  font-weight: 600;
`;

export const Faq = styled.div`
  margin-top: 3.2rem;
  border-bottom: 0.1rem solid rgba(20, 74, 43, 0.12);
`;

export const FaqHeading = styled.h2`
  font-size: 2rem;
  font-weight: 700;
  color: #144a2b;
  margin-bottom: 1.4rem;
`;

export const FaqItem = styled.button`
  width: 100%;
  background: none;
  border: none;
  border-top: 0.1rem solid rgba(20, 74, 43, 0.12);
  padding: 1.6rem 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1.6rem;
  cursor: pointer;
  text-align: left;
`;

export const FaqQuestion = styled.span`
  font-size: 1.4rem;
  color: #132018;
  line-height: 1.45;
`;

export const FaqIcon = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  transition: transform 0.2s;
  transform: rotate(${p => (p.$open ? '180deg' : '0deg')});
  &::before {
    font-family: 'icomoon';
    content: '\\e910';
    font-size: 2.2rem;
    font-weight: normal;
    color: #1c6b3a;
  }
`;

export const FaqAnswer = styled.div`
  font-size: 1.3rem;
  color: #425147;
  line-height: 1.65;
  padding-bottom: 1.4rem;
  padding-right: 4rem;
  /* Stays in the DOM (and the SSR'd HTML) even when collapsed, so search engines can still
     index it — only visually hidden, not conditionally rendered. */
  display: ${p => (p.$open ? 'block' : 'none')};
`;

export const FooterCta = styled.div`
  background: #0f3b22;
  padding: 2.8rem 3.2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 2.4rem;
  flex-wrap: wrap;

  ${media.small`
    padding: 2.4rem 2rem;
  `};
`;

export const FooterCtaText = styled.div`
  color: ${p => p.theme.primaryTextColor};
  font-size: 1.9rem;
  font-weight: 700;
  line-height: 1.25;
`;

// Light-on-dark button used only on the dark green CTA band — the page's
// other buttons (hero, category CTA) sit on a lighter background and use
// PrimaryButton's dark-on-light treatment instead.
export const FooterCtaButton = styled(Link)`
  background: #3fa65c;
  color: #0f3b22;
  font-size: 1.4rem;
  font-weight: 700;
  padding: 1.3rem 2.8rem;
  border-radius: 0.6rem;
  white-space: nowrap;
  text-decoration: none;
  display: inline-block;

  &:hover {
    background: #37934f;
  }
`;

export const SubText = styled.div`
  padding: 2rem 0 3rem;
  font-size: 1.25rem;
  color: #6b7570;
  font-style: italic;
`;
